import { api, UploadedMedia } from '../../../lib/api';
import { Message } from '../../../types';

export const MESSAGE_PAGE_SIZE = 50;

// Map snake_case API response to camelCase frontend Message type
function mapMessage(raw: Record<string, any>): Message {
  return {
    id: raw.id,
    conversationId: raw.session_id ?? raw.conversationId ?? '',
    sender: raw.sender_type ?? raw.sender ?? '',
    senderId: raw.sender_id ?? raw.senderId ?? '',
    senderName: raw.sender_name ?? raw.senderName,
    content: raw.content ?? '',
    mediaUrl: raw.media_url ?? raw.mediaUrl,
    mediaType: raw.media_type ?? raw.mediaType,
    fileName: raw.file_name ?? raw.fileName,
    status: raw.status ?? 'sent',
    metadata: raw.message_metadata ?? raw.metadata,
    createdAt: raw.created_at ?? raw.createdAt,
    editedAt: raw.edited_at ?? raw.editedAt,
    editedByName: raw.edited_by_name ?? raw.editedByName,
    deletedAt: raw.deleted_at ?? raw.deletedAt,
    deletedByName: raw.deleted_by_name ?? raw.deletedByName,
  };
}

export interface MutationResult {
  message: Message;
  channelPropagated: boolean;
}

export const chatService = {
  async getMessages(conversationId: string, skip = 0, limit = MESSAGE_PAGE_SIZE): Promise<Message[]> {
    const data = await api.get<Record<string, any>[]>(`/conversations/${conversationId}/messages`, {
      params: { skip, limit },
    });
    return data.map(mapMessage);
  },

  async sendMessage(
    conversationId: string,
    content: string,
    media?: UploadedMedia
  ): Promise<Message> {
    const body: Record<string, any> = { content };
    if (media) {
      body.media_url = media.mediaUrl;
      body.media_type = media.mediaType;
      body.media_mime_type = media.mimeType;
      body.file_name = media.fileName;
    }
    const data = await api.post<Record<string, any>>(
      `/conversations/${conversationId}/messages`,
      body
    );
    return mapMessage(data);
  },

  async markAsRead(conversationId: string): Promise<void> {
    return api.post<void>(`/conversations/${conversationId}/read`);
  },

  async editMessage(messageId: string, content: string): Promise<MutationResult> {
    const data = await api.patch<Record<string, any>>(`/messages/${messageId}`, { content });
    return {
      message: mapMessage(data.message),
      channelPropagated: !!data.channel_propagated,
    };
  },

  async deleteMessage(messageId: string): Promise<MutationResult> {
    const data = await api.delete<Record<string, any>>(`/messages/${messageId}`);
    return {
      message: mapMessage(data.message),
      channelPropagated: !!data.channel_propagated,
    };
  },
};
